import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { useTheme } from '../theme';
import { logAnalyticsEvent } from '../firebase';

interface CodeBlockProps {
  code: string;
  language?: string; // shown as a label, e.g. "JavaScript"
}

// A simple, dependency-free code block with a copy button. No syntax
// highlighter is pulled in (keeps the bundle small and prerendering fast);
// the monospace styling and label are enough for short snippets.
export default function CodeBlock({ code, language }: CodeBlockProps) {
  const { darkMode } = useTheme();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      logAnalyticsEvent('copy_code', { language });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      /* clipboard may be unavailable; ignore */
    }
  };

  return (
    <div className={`relative rounded-xl border overflow-hidden ${darkMode ? 'bg-slate-950 border-slate-800' : 'bg-slate-900 border-slate-800'}`}>
      <div className={`flex items-center justify-between px-4 py-2 text-xs font-medium ${darkMode ? 'bg-slate-800/60 text-slate-300' : 'bg-slate-800 text-slate-300'}`}>
        <span>{language}</span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 hover:text-white transition-colors"
          aria-label="Copy code"
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="p-4 overflow-x-auto text-sm leading-relaxed text-gray-100">
        <code>{code}</code>
      </pre>
    </div>
  );
}
